/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * HintCard.tsx — Hin\1 \2ard (services/operator-web-app).
 */

import { useOperatorStore } from '@/store';
import { Button, Badge } from '@/components/ui';
import { sendFeedback } from '@/api/feedback';
import type { Hint } from '@/store/slices/copilotSlice';
import { useTranslation } from '@/i18n';

/** E6-S4: Below this confidence we show fallback UI (low-confidence notice). */
export const LOW_CONFIDENCE_THRESHOLD = 0.5;

export interface HintCardProps {
  hint: Hint;
}

/**
 * E6-S2: Priority hint card with text, Why panel (intent/confidence/reason), actions Copy / Used / Irrelevant.
 * E6-S4: Fallback UI for low-confidence — badge and muted styling.
 * Plan: 08, 14 wireframes.
 */
export function HintCard({ hint }: HintCardProps) {
  const t = useTranslation();
  const markHintUsed = useOperatorStore((s) => s.markHintUsed);
  const markHintIrrelevant = useOperatorStore((s) => s.markHintIrrelevant);
  const callId = useOperatorStore((s) => s.call.callId);
  const sessionId = useOperatorStore((s) => s.call.sessionId);

  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(hint.text);
    }
    if (callId) {
      sendFeedback({
        call_id: callId,
        session_id: sessionId ?? undefined,
        type: 'hint_copied',
        hint: hint.text,
      }).catch(() => {});
    }
  };

  const handleUsed = () => {
    markHintUsed(hint.hintId);
  };

  const handleIrrelevant = () => {
    markHintIrrelevant(hint.hintId);
  };

  const confidencePct = Math.round(hint.confidence * 100);
  const isLowConfidence = hint.confidence < LOW_CONFIDENCE_THRESHOLD;

  return (
    <article
      className={`rounded-lg border p-3 space-y-3 ${
        isLowConfidence
          ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/20'
          : 'border-sky-200 dark:border-sky-800 bg-sky-50/50 dark:bg-sky-900/20'
      }`}
      aria-label={t('hint.label')}
      data-hint-id={hint.hintId}
      data-low-confidence={isLowConfidence || undefined}
    >
      {isLowConfidence && (
        <Badge variant="warning" className="mb-1">
          {t('hint.low_confidence')}
        </Badge>
      )}
      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
        {hint.text}
      </p>

      {/* Why panel: intent, confidence, reason */}
      <div
        className="rounded border px-2.5 py-2 text-xs space-y-1"
        style={{ borderColor: 'var(--glass-border)', background: 'var(--glass-bg-from)' }}
        aria-label={t('hint.why_label')}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span style={{ color: 'var(--text-muted)' }}>{t('hint.intent')}</span>
          <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>
            {hint.intent}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>
            {t('hint.confidence', { percent: confidencePct })}
          </span>
        </div>
        {hint.why && (
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            {hint.why}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={handleCopy}>
          {t('hint.copy')}
        </Button>
        <Button variant="primary" size="sm" onClick={handleUsed}>
          {t('hint.used')}
        </Button>
        <Button variant="ghost" size="sm" onClick={handleIrrelevant}>
          {t('hint.irrelevant')}
        </Button>
      </div>
    </article>
  );
}
